import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import walletService from "../../services/walletService";
import orbitxService from "../../services/orbitxService";
import quickSwapService from "../../services/quickSwapService";
import SwapConfirmationModal from "./SwapConfirmationModal";
import TransactionHistory from "./TransactionHistory";
import SwapAmountInput from "./SwapAmountInput";
import useAuthStore from "../../store/useAuthStore";
import { Link } from "react-router-dom";

const BuyFromWallet = () => {
  const { isAuthenticated } = useAuthStore();
  const [walletBalances, setWalletBalances] = useState({});
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [sellAmount, setSellAmount] = useState("");
  const [buyAmount, setBuyAmount] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [btc1Price, setBtc1Price] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [availableCoins, setAvailableCoins] = useState({});
  const [isExecuting, setIsExecuting] = useState(false);
  const [swapCalculation, setSwapCalculation] = useState(null);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapData, setSwapData] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadInitialData = async () => {
      setLoading(true);
      try {
        await fetchBTC1Price();
        if (isAuthenticated) {
          await fetchWalletBalances();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadInitialData();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Set default currency once coins are loaded
  useEffect(() => {
    if (Object.keys(availableCoins).length === 0 || selectedCurrency) return;

    const walletCurrencies = Object.keys(walletBalances).filter(
      (currency) =>
        walletBalances[currency]?.available > 0 && currency !== "BTC1",
    );
    const defaultCurrency =
      walletCurrencies.find((currency) => availableCoins[currency]) ||
      Object.keys(availableCoins).find((currency) => currency !== "BTC1");

    if (defaultCurrency) {
      setSelectedCurrency(defaultCurrency);
    }
  }, [availableCoins, walletBalances, selectedCurrency]);

  // Sync receive/pay amounts when price data becomes available
  useEffect(() => {
    if (
      !selectedCurrency ||
      !btc1Price ||
      Object.keys(availableCoins).length === 0
    ) {
      return;
    }

    if (sellAmount && parseFloat(sellAmount) > 0) {
      const calculatedBuy = calculateBuyAmount(sellAmount);
      if (calculatedBuy) {
        setBuyAmount(calculatedBuy);
      }
    } else if (buyAmount && parseFloat(buyAmount) > 0) {
      const calculatedSell = calculateSellAmount(buyAmount);
      if (calculatedSell) {
        setSellAmount(calculatedSell);
      }
    }
  }, [availableCoins, selectedCurrency, btc1Price]);

  // Recalculate when availableCoins or selectedCurrency changes
  useEffect(() => {
    if (Object.keys(availableCoins).length > 0 && selectedCurrency) {
      calculateSwapAmounts();
    }
  }, [availableCoins, selectedCurrency, btc1Price, sellAmount, buyAmount]);

  // Calculate swap amounts using the API
  const calculateSwapAmounts = async () => {
    if (!selectedCurrency || !btc1Price) {
      console.log("Missing required data for calculation:", {
        selectedCurrency,
        btc1Price,
      });
      return;
    }

    // Check if user is authenticated
    const token = localStorage.getItem("access_token");
    if (!token) {
      console.log("User not authenticated, skipping calculation");
      return;
    }

    try {
      if (sellAmount && sellAmount !== "") {
        const sellNum = parseFloat(sellAmount);
        if (!isNaN(sellNum) && sellNum > 0) {
          console.log("Calculating swap amounts for sell:", {
            selectedCurrency,
            sellNum,
            btc1Price,
          });
          const calculation = await quickSwapService.calculateSwapAmounts(
            selectedCurrency,
            sellNum,
            btc1Price,
          );
          setSwapCalculation(calculation);
          setBuyAmount(calculation.net_btc1_amount.toFixed(6));
        }
      } else if (buyAmount && buyAmount !== "") {
        const buyNum = parseFloat(buyAmount);
        if (!isNaN(buyNum) && buyNum > 0) {
          // Calculate reverse: how much source currency needed for this BTC1 amount
          const selectedCoinPrice =
            availableCoins[selectedCurrency]?.price ||
            getStaticPrice(selectedCurrency);
          const currentBtc1Price = btc1Price || 108;

          if (selectedCoinPrice && currentBtc1Price) {
            const buyValueInUSD = buyNum * currentBtc1Price;
            const sellAmount = buyValueInUSD / selectedCoinPrice;
            setSellAmount(sellAmount.toFixed(6));

            // Recalculate with the new sell amount
            console.log("Calculating swap amounts for buy:", {
              selectedCurrency,
              sellAmount,
              btc1Price,
            });
            const calculation = await quickSwapService.calculateSwapAmounts(
              selectedCurrency,
              parseFloat(sellAmount),
              btc1Price,
            );
            setSwapCalculation(calculation);
          }
        }
      }
    } catch (error) {
      console.error("Error calculating swap amounts:", error);

      // Fallback to local calculation if API fails
      try {
        console.log("API failed, using fallback calculation");
        if (sellAmount && sellAmount !== "") {
          const sellNum = parseFloat(sellAmount);
          if (!isNaN(sellNum) && sellNum > 0) {
            const selectedCoinPrice =
              availableCoins[selectedCurrency]?.price ||
              getStaticPrice(selectedCurrency);
            const currentBtc1Price = btc1Price || 108;

            if (selectedCoinPrice && currentBtc1Price) {
              const sellValueInUSD = sellNum * selectedCoinPrice;
              const btc1Amount = sellValueInUSD / currentBtc1Price;
              const feeAmount = sellValueInUSD * 0.0; // 0% fee
              const netBtc1Amount = btc1Amount * 1.0; // After fee

              setSwapCalculation({
                source_amount: sellNum,
                source_currency: selectedCurrency,
                source_price_usd: selectedCoinPrice,
                usd_value: sellValueInUSD,
                btc1_amount: btc1Amount,
                btc1_price_usd: currentBtc1Price,
                exchange_rate: selectedCoinPrice / currentBtc1Price,
                fee_percentage: 0.0,
                fee_amount: feeAmount,
                net_btc1_amount: netBtc1Amount,
              });
              setBuyAmount(netBtc1Amount.toFixed(6));
            }
          }
        }
      } catch (fallbackError) {
        console.error("Fallback calculation also failed:", fallbackError);
        setError(
          "Failed to calculate swap amounts: " +
            (error.message || "Unknown error"),
        );
      }
    }
  };

  const fetchWalletBalances = async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      const balances = await walletService.getBalances();
      setWalletBalances(balances);

      // Set first available currency as default (excluding BTC1)
      const availableCurrencies = Object.keys(balances).filter(
        (currency) => balances[currency].available > 0 && currency !== "BTC1",
      );
      if (availableCurrencies.length > 0) {
        setSelectedCurrency((current) => current || availableCurrencies[0]);
      }
    } catch (err) {
      setError("Failed to load wallet balances");
      console.error("Error fetching wallet balances:", err);
    }
  };

  const fetchBTC1Price = async () => {
    try {
      let btc1PriceFromQuickSwap = null;

      // First, get the custom BTC1 price from the quick swap API
      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";
      const btc1PriceResponse = await fetch(
        `${baseUrl}/v1/quick-swap/btc1-price/`,
      );
      if (btc1PriceResponse.ok) {
        const btc1PriceData = await btc1PriceResponse.json();
        if (btc1PriceData.price) {
          btc1PriceFromQuickSwap = btc1PriceData.price;
          setBtc1Price(btc1PriceData.price);
          console.log(
            `BTC1 custom price from quick swap API: ${btc1PriceData.price}`,
          );
        }
      } else {
        console.warn(
          "Failed to fetch custom BTC1 price, falling back to coins API",
        );
      }

      // Also get coins data for available currencies and BTC price
      const coinsData = await orbitxService.getAvailableCoins();

      // Store all available coins for currency selection
      setAvailableCoins(coinsData.coins || {});

      // Get BTC price for exchange rate calculation
      const btcUsdtPair = coinsData.pairs_data?.["BTC-USDT"];
      const currentBtc1Price = btc1PriceFromQuickSwap || btc1Price;
      if (btcUsdtPair?.price && currentBtc1Price) {
        const btcBtc1Rate = btcUsdtPair.price / currentBtc1Price;
        setExchangeRate(btcBtc1Rate);
        console.log(
          `BTC/USDT: ${btcUsdtPair.price}, BTC1/USDT: ${currentBtc1Price}, BTC/BTC1: ${btcBtc1Rate}`,
        );
      }

      // If we didn't get BTC1 price from quick swap API, fallback to coins data
      if (!btc1PriceFromQuickSwap) {
        const btc1UsdtPair = coinsData.pairs_data?.["BTC1-USDT"];
        if (btc1UsdtPair?.price) {
          setBtc1Price(btc1UsdtPair.price);
          console.log(
            `BTC1 price from pairs data (fallback): ${btc1UsdtPair.price}`,
          );
        } else {
          // Final fallback
          const btc1CoinData = coinsData.coins?.BTC1;
          if (
            btc1CoinData?.price !== null &&
            btc1CoinData?.price !== undefined
          ) {
            setBtc1Price(btc1CoinData.price);
            console.log(
              `BTC1 price from coins data (fallback): ${btc1CoinData.price}`,
            );
          } else {
            console.log(
              "No BTC1 price data available from API, using fallback",
            );
            setBtc1Price(108.34);
          }
        }
      }

      setError(""); // Clear any previous errors
    } catch (err) {
      console.error("Error fetching BTC1 price:", err);
      // Use fallback values on API error
      setBtc1Price(108.34);
      setError(""); // Clear error since we have a fallback
    }
  };

  // Format large numbers with commas
  const formatLargeNumber = (num) => {
    if (!num || num === "") return "";

    // Preserve the original string format if it's zero with decimals (e.g., "0.000")
    const number = parseFloat(num);
    if (isNaN(number)) {
      // If it's not a valid number but has content, return as-is (might be partial input like "0.")
      return num;
    }

    // If the number is zero and the original string contains a decimal point, preserve the format
    if (number === 0 && typeof num === "string" && num.includes(".")) {
      // Extract the decimal part
      const parts = num.split(".");
      const decimalPart = parts[1] || "";
      // Preserve the original decimal places (up to 6)
      const preservedDecimals = decimalPart.substring(0, 6);
      // Return with preserved decimal places
      return `0.${preservedDecimals}`;
    }

    // For very large numbers, show without decimals
    if (number >= 1000000) {
      return number.toLocaleString("en-US", {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });
    }

    // For smaller numbers, show with appropriate decimals
    return number.toLocaleString("en-US", {
      maximumFractionDigits: 6,
      minimumFractionDigits: 0,
    });
  };

  // Calculate buy amount when sell amount changes
  const calculateBuyAmount = (sellValue) => {
    if (!sellValue || !selectedCurrency) return "";
    const sellNum = parseFloat(sellValue);
    if (isNaN(sellNum) || sellNum <= 0) return "";

    // Get the selected currency price (use static fallback if null)
    const selectedCoinPrice =
      availableCoins[selectedCurrency]?.price ||
      getStaticPrice(selectedCurrency);
    const currentBtc1Price = btc1Price || 108; // Static fallback for BTC1

    if (!selectedCoinPrice || !currentBtc1Price) return "";

    // Calculate: (sellAmount * selectedCurrencyPrice) / BTC1Price
    const sellValueInUSD = sellNum * selectedCoinPrice;
    const btc1Amount = sellValueInUSD / currentBtc1Price;
    return btc1Amount.toFixed(6);
  };

  // Calculate sell amount when buy amount changes
  const calculateSellAmount = (buyValue) => {
    if (!buyValue || !selectedCurrency) return "";
    const buyNum = parseFloat(buyValue);
    if (isNaN(buyNum) || buyNum <= 0) return "";

    // Get the selected currency price (use static fallback if null)
    const selectedCoinPrice =
      availableCoins[selectedCurrency]?.price ||
      getStaticPrice(selectedCurrency);
    const currentBtc1Price = btc1Price || 108; // Static fallback for BTC1

    if (!selectedCoinPrice || !currentBtc1Price) return "";

    // Calculate: (buyAmount * BTC1Price) / selectedCurrencyPrice
    const buyValueInUSD = buyNum * currentBtc1Price;
    const sellAmount = buyValueInUSD / selectedCoinPrice;
    return sellAmount.toFixed(6);
  };

  // Static price fallbacks for when API data is null
  const getStaticPrice = (currency) => {
    const staticPrices = {
      BTC: 0.0, // Updated from API data
      ETH: 0.0, // Updated from API data
      USDT: 0.0,
      USDC: 0.0,
      DAI: 0.0,
      BNB: 0.0,
      MATIC: 0.0,
      FTT: 0.0, // Updated from API data
      TRX: 0.0,
    };
    return staticPrices[currency] || 0.0;
  };

  const handleSellAmountChange = (value) => {
    setSellAmount(value);
    setError("");

    if (!value || value.trim() === "" || value === "0") {
      setBuyAmount("");
      setSwapCalculation(null);
      return;
    }

    const numValue = parseFloat(value);
    if (numValue === 0 && !value.includes(".")) {
      setBuyAmount("");
      setSwapCalculation(null);
      return;
    }

    if (value.endsWith(".") || value === "0.") {
      setBuyAmount("");
      setSwapCalculation(null);
      return;
    }

    setBuyAmount(calculateBuyAmount(value));
  };

  const handleBuyAmountChange = (value) => {
    setBuyAmount(value);
    setError("");

    if (!value || value.trim() === "" || value === "0") {
      setSellAmount("");
      setSwapCalculation(null);
      return;
    }

    const numValue = parseFloat(value);
    if (numValue === 0 && !value.includes(".")) {
      setSellAmount("");
      setSwapCalculation(null);
      return;
    }

    if (value.endsWith(".") || value === "0.") {
      setSellAmount("");
      setSwapCalculation(null);
      return;
    }

    setSellAmount(calculateSellAmount(value));
  };

  const handleCurrencyChange = (newCurrency) => {
    setSelectedCurrency(newCurrency);

    // Recalculate amounts when currency changes using the new currency
    if (sellAmount && sellAmount !== "") {
      const sellNum = parseFloat(sellAmount);
      if (!isNaN(sellNum) && sellNum > 0) {
        const selectedCoinPrice =
          availableCoins[newCurrency]?.price || getStaticPrice(newCurrency);
        const currentBtc1Price = btc1Price || 108;

        if (selectedCoinPrice && currentBtc1Price) {
          const sellValueInUSD = sellNum * selectedCoinPrice;
          const btc1Amount = sellValueInUSD / currentBtc1Price;
          setBuyAmount(btc1Amount.toFixed(6));
        }
      }
    } else if (buyAmount && buyAmount !== "") {
      const buyNum = parseFloat(buyAmount);
      if (!isNaN(buyNum) && buyNum > 0) {
        const selectedCoinPrice =
          availableCoins[newCurrency]?.price || getStaticPrice(newCurrency);
        const currentBtc1Price = btc1Price || 108;

        if (selectedCoinPrice && currentBtc1Price) {
          const buyValueInUSD = buyNum * currentBtc1Price;
          const sellAmount = buyValueInUSD / selectedCoinPrice;
          setSellAmount(sellAmount.toFixed(6));
        }
      }
    }
  };

  const handleSwap = async () => {
    console.log("handleSwap called"); // Debug log

    if (!selectedCurrency || !sellAmount || parseFloat(sellAmount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    const balance = walletBalances[selectedCurrency];
    if (parseFloat(sellAmount) > balance.available) {
      setError("Insufficient balance");
      return;
    }

    if (!btc1Price) {
      setError("Unable to get BTC1 price");
      return;
    }

    try {
      setIsExecuting(true);
      setError("");

      const result = await quickSwapService.executeQuickSwap(
        selectedCurrency,
        parseFloat(sellAmount),
        btc1Price,
      );

      console.log("Swap result:", result); // Debug log

      if (result.success) {
        console.log("Swap successful, showing toast"); // Debug log

        // Prepare swap data for modal
        const swapModalData = {
          from_amount: parseFloat(sellAmount),
          from_currency: selectedCurrency,
          to_amount: parseFloat(result.to_amount),
          exchange_rate:
            swapCalculation?.exchange_rate ||
            parseFloat(sellAmount) / parseFloat(result.to_amount),
          fee_percentage: swapCalculation?.fee_percentage || 0,
          fee_amount: swapCalculation?.fee_amount || 0,
          transaction_id: result.transaction_id || result.tx_hash || "N/A",
          timestamp: new Date().toISOString(),
        };

        // Store swap data and show modal
        setSwapData(swapModalData);
        setShowSwapModal(true);

        // Show success toast notification
        toast.success(
          `Successfully swapped ${sellAmount} ${selectedCurrency} for ${result.to_amount} BTC1`,
          {
            icon: "🎉",
          },
        );

        // Reset form
        setSellAmount("");
        setBuyAmount("");
        setSwapCalculation(null);

        // Refresh wallet balances
        await fetchWalletBalances();
      } else {
        console.log("Swap failed:", result.error); // Debug log
        const errorMessage = result.error || "Swap failed";
        setError(errorMessage);
        toast.error(errorMessage, {
          icon: "❌",
        });
      }
    } catch (error) {
      console.error("Error executing swap:", error);
      const errorMessage = error.error || "Failed to execute swap";
      setError(errorMessage);
      toast.error(errorMessage, {
        icon: "❌",
      });
    } finally {
      setIsExecuting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="loading-spinner mx-auto mb-4"></div>
        <p className="text-white">Loading wallet balances...</p>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 lg:p-8 mt-12 sm:mt-0">
      <div className="max-w-md sm:max-w-lg lg:max-w-3xl mx-auto">
        <div className="bg-gray-900 rounded-lg p-3 sm:p-4 lg:p-6">
          <div className="space-y-3 sm:space-y-4">
            {/* Sell Section */}
            <div className="bg-gray-800 rounded-lg p-3 sm:p-4 lg:p-5 h-32 sm:h-36 lg:h-44 flex flex-col justify-between">
              <label className="block text-xs sm:text-sm font-medium text-white mb-2 sm:mb-3">
                You Pay
              </label>
              <div className="flex items-center justify-between mb-2">
                <div className="w-full max-w-[200px] sm:max-w-[300px] lg:max-w-[500px] overflow-hidden">
                  <SwapAmountInput
                    value={sellAmount}
                    onChange={handleSellAmountChange}
                    formatLargeNumber={formatLargeNumber}
                  />
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2 bg-gray-600 rounded-lg p-1.5 sm:p-2 relative z-50 min-w-[80px] sm:min-w-[100px]">
                  <select
                    value={selectedCurrency}
                    onChange={(e) => handleCurrencyChange(e.target.value)}
                    className="bg-transparent text-white border-none focus:outline-none text-xs sm:text-sm font-medium appearance-none cursor-pointer pr-4 sm:pr-6 z-50 w-full"
                    style={{ zIndex: 9999 }}
                  >
                    {Object.keys(availableCoins)
                      .filter((currency) => currency !== "BTC1")
                      .map((currency) => (
                        <option
                          key={currency}
                          value={currency}
                          className="bg-gray-800 text-white text-xs sm:text-sm py-1 px-2"
                        >
                          {currency}
                        </option>
                      ))}
                  </select>
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 text-white absolute right-1 sm:right-2 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex justify-between text-xs sm:text-sm text-white">
                <span className="truncate">
                  $
                  {sellAmount
                    ? (
                        parseFloat(sellAmount) *
                        (availableCoins[selectedCurrency]?.price ||
                          getStaticPrice(selectedCurrency))
                      ).toFixed(2)
                    : "0"}
                </span>
                <span className="text-white truncate ml-2">
                  {walletBalances[selectedCurrency]?.available || 0}{" "}
                  {selectedCurrency}
                </span>
              </div>
            </div>

            {/* Swap Arrow */}
            <div className="flex justify-center">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#0E1696] !text-white rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5 !text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                  />
                </svg>
              </div>
            </div>

            {/* Buy Section */}
            <div className="bg-gray-800 rounded-lg p-3 sm:p-4 lg:p-5 mb-3 sm:mb-4 h-32 sm:h-36 lg:h-44 flex flex-col justify-between">
              <label className="block text-xs sm:text-sm font-medium text-white mb-2 sm:mb-3">
                You Receive
              </label>
              <div className="flex items-center justify-between mb-2">
                <div className="w-full max-w-[200px] sm:max-w-[300px] lg:max-w-[500px] overflow-hidden">
                  <SwapAmountInput
                    value={buyAmount}
                    onChange={handleBuyAmountChange}
                    formatLargeNumber={formatLargeNumber}
                  />
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2 bg-gray-600 rounded-lg px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 min-w-[80px] sm:min-w-[100px]">
                  <span className="text-sm sm:text-lg text-white">₿</span>
                  <span className="text-white text-sm sm:text-lg font-medium ml-1 sm:ml-2">
                    BTC1
                  </span>
                </div>
              </div>
              <div className="flex justify-between text-xs sm:text-sm text-white">
                <span className="truncate">
                  $
                  {buyAmount
                    ? (parseFloat(buyAmount) * btc1Price).toFixed(2)
                    : "0"}
                </span>
                <span className="text-white truncate ml-2">
                  ${btc1Price} per BTC1
                </span>
              </div>
            </div>

            {/* Swap Details */}
            {swapCalculation && (
              <div className="bg-gray-800 rounded-lg p-3 sm:p-4 space-y-2">
                <div className="flex justify-between text-xs sm:text-sm text-white">
                  <span>Digital Asset Marketplace Rate:</span>
                  <span>
                    1 BTC1 = {1 / swapCalculation.exchange_rate.toFixed(6)}{" "}
                    {selectedCurrency}
                  </span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm text-white">
                  <span>Fee ({swapCalculation.fee_percentage}%):</span>
                  <span>${swapCalculation.fee_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm text-white">
                  <span>You will receive:</span>
                  <span className="text-white font-medium">
                    {swapCalculation.net_btc1_amount.toFixed(6)} BTC1
                  </span>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-900/20 border border-red-700 rounded-lg p-2 sm:p-3">
                <p className="text-red-400 text-xs sm:text-sm">{error}</p>
              </div>
            )}

            {/* Swap Button */}
            <button
              onClick={handleSwap}
              disabled={
                !selectedCurrency ||
                !sellAmount ||
                parseFloat(sellAmount) <= 0 ||
                isExecuting
              }
              className="w-full bg-[#0E1696] hover:bg-[#0E1696] !text-white py-3 sm:py-4 text-sm sm:text-lg font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isExecuting
                ? "Executing Swap..."
                : `Swap ${selectedCurrency} for BTC1`}
            </button>
          </div>
        </div>
      </div>

      {/* Transaction History Section */}
      <div className="mt-8">
        <TransactionHistory type="swap" limit={5} showTitle={true} />
      </div>

      {/* Swap Confirmation Modal */}
      <SwapConfirmationModal
        isOpen={showSwapModal}
        onClose={() => setShowSwapModal(false)}
        swapData={swapData}
      />
    </div>
  );
};

export default BuyFromWallet;
